import { useEffect } from "react";

import { RootComponentInstance } from "@uniformdev/canvas";
import { UniformEntryPage } from "src/pages/uniform-entry/uniform-entry-page";
import { useWAEContext } from "src/uniform/utils/wae-context-provider";

import FeatureGates from "@atlaskit/feature-gate-js-client";
import UFOSegment from "@atlaskit/react-ufo/segment";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { CrossFlowTouchpointProvider } from "@/components/cross-flow/touchpoint";
import { hasJiraImcCampaignKey } from "@/components/signup/utils";
import { useLoggedInHasJira, useLoggedOut } from "@/components/user-context";
import { IntercomProvider } from "@/libraries/intercom/src";
import { LoggedInHeroContainer } from "@/templates/software/jira/logged-in/organic-logged-in/logged-in-hero-container";
import { OrganicLoggedInJiraProductTourOverview } from "@/templates/software/jira/logged-in/organic-logged-in/organic-logged-in-jira-product-tour-overview.template";
import { LoggedOutOrNoJiraProductTourOverview } from "@/templates/software/jira/logged-out/jira-software-logged-out-page/src";
import {
    EnrollmentType,
    experiments,
    useExperiment,
    useExperimentExposure
} from "@/utils/experiment";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { JiraProductTourAiTemplate } from "../../../../templates/paid-landing/src/cfind-4666-jira-product-tour-ai.template";

export const JiraSoftwarePage = ({ utmCampaign }: { utmCampaign?: string }) => {
    const loggedInHasJira = useLoggedInHasJira();
    const isLoggedOut = useLoggedOut();
    const isWae = checkFeatureFlagGate(
        featureFlags.dxpMigrationJiraProductTourGate
    );

    const loggedInSkeletonEnabled = checkFeatureFlagGate(
        featureFlags.jfindLoggedInJiraProductTourSkeleton
    );

    // FYI, this is currently JA only, since we only have JA work space appId
    const isBootIntercom: boolean = FeatureGates.checkGate(
        "gwp-737-enable-intercom-chat-jsw"
    );

    const hasImcKey = hasJiraImcCampaignKey(utmCampaign);

    // Jira FFl experiment check start
    const jiraFflExperimentCheck = useExperiment(experiments.cfind4666JiraFfl);
    const isJiraFflVariant =
        jiraFflExperimentCheck.type === EnrollmentType.ENROLLED &&
        jiraFflExperimentCheck.value;

    // Jira FFl experiment check end

    let template: React.ReactNode = <LoggedOutOrNoJiraProductTourOverview />;

    //For logged in, we will either show a "Has Jira" experience, otherwise the Uniform "logged out" page
    if (loggedInHasJira) {
        template = <OrganicLoggedInJiraProductTourOverview />;
    } else {
        if (hasImcKey && isJiraFflVariant && isLoggedOut) {
            template = <JiraProductTourAiTemplate />;
        } else if (isWae) {
            template = <WaeJiraPage />;
        }
    }

    return (
        <UFOSegment name="jira-product-tour-overview">
            <IntercomProvider appId={"p0ww63i2"} triggerBoot={isBootIntercom}>
                <CrossFlowTouchpointProvider
                    productKeys={[ProductKeys.JIRA_SOFTWARE]}
                    screenName="wacJswProductTourPage"
                >
                    {loggedInSkeletonEnabled ? ( //Feature Gate checks for logged in users
                        <LoggedInHeroContainer>
                            {template}
                        </LoggedInHeroContainer>
                    ) : (
                        template
                    )}
                </CrossFlowTouchpointProvider>
            </IntercomProvider>
        </UFOSegment>
    );
};

const WaeJiraPage = () => {
    const { composition } = useWAEContext();

    const [jiraFflExposureFired, fireJiraFflExposure] = useExperimentExposure(
        experiments.cfind4666JiraFfl
    );

    useEffect(() => {
        if (!jiraFflExposureFired) {
            fireJiraFflExposure();
        }
    }, [jiraFflExposureFired, fireJiraFflExposure]);

    return (
        <UniformEntryPage composition={composition as RootComponentInstance} />
    );
};
